/**
 * ArtifactChip - Clickable chip that references an artifact embedded in agent messages
 *
 * Parses [[ARTIFACT:TYPE:ID:LABEL]] syntax and renders an interactive chip.
 * Clicking opens the ArtifactPanel with the referenced artifact data.
 */

import React from 'react';
import { Chip } from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  Hotel as HotelIcon,
  Place as PlaceIcon,
  Flight as FlightIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';

function getArtifactIcon(type) {
  switch (type) {
    case 'TRIP_TIMELINE': return <AccountTreeIcon sx={{ fontSize: '0.875rem' }} />;
    case 'ACCOMMODATION':
    case 'ENTITY_VIEW_accommodation': return <HotelIcon sx={{ fontSize: '0.875rem' }} />;
    case 'PLACE':
    case 'ENTITY_VIEW_place': return <PlaceIcon sx={{ fontSize: '0.875rem' }} />;
    case 'TRAVEL':
    case 'ENTITY_VIEW_travel': return <FlightIcon sx={{ fontSize: '0.875rem' }} />;
    case 'DAY':
    case 'ENTITY_VIEW_day': return <CalendarIcon sx={{ fontSize: '0.875rem' }} />;
    default: return <AccountTreeIcon sx={{ fontSize: '0.875rem' }} />;
  }
}

function getArtifactColor(type) {
  switch (type) {
    case 'TRIP_TIMELINE': return 'primary';
    case 'ACCOMMODATION':
    case 'ENTITY_VIEW_accommodation': return 'secondary';
    case 'PLACE':
    case 'ENTITY_VIEW_place': return 'success';
    case 'TRAVEL':
    case 'ENTITY_VIEW_travel': return 'info';
    default: return 'default';
  }
}

/**
 * ArtifactChip Component
 *
 * @param {Object} props
 * @param {string} props.type - Artifact type (e.g. TRIP_TIMELINE, ENTITY_VIEW)
 * @param {string} props.id - Artifact identifier
 * @param {string} props.label - Display label
 * @param {Function} props.onClick - Called with { type, id, label } when clicked
 */
const ArtifactChip = ({ type, id, label, onClick }) => {
  const handleClick = () => {
    if (onClick) {
      onClick({ type, id, label });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <Chip
      icon={getArtifactIcon(type)}
      label={label || type}
      size="small"
      color={getArtifactColor(type)}
      variant="outlined"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{
        cursor: 'pointer',
        fontSize: '0.75rem',
        height: '22px',
        mx: 0.25,
        verticalAlign: 'middle',
        '&:hover': {
          opacity: 0.8
        }
      }}
      title={`View ${label || type} artifact`}
    />
  );
};

export default ArtifactChip;
